import { morph } from '@theme/morph';
import { Component } from '@theme/component';
import { CartUpdateEvent, ThemeEvents } from '@theme/events';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, isMobileBreakpoint } from '@theme/utilities';

function parseTrackingJson(value) {
  if (!value) return {};

  try {
    return JSON.parse(value);
  }
  catch (_error) {
    return {};
  }
}

async function fetchProductPageHtml(productPageUrl, signal) {
  if (!productPageUrl) return null;

  const response = await fetch(productPageUrl, { signal });

  if (!response.ok) {
    throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
  }

  const responseText = await response.text();
  return new DOMParser().parseFromString(responseText, 'text/html');
}

function createViewProductDetailsLink(productPageUrl) {
  const viewDetailsLink = document.createElement('a');
  viewDetailsLink.href = productPageUrl;
  viewDetailsLink.textContent = 'View All details';
  viewDetailsLink.classList.add('view-product-details-link');
  viewDetailsLink.style.setProperty('display', 'block', 'important');

  return viewDetailsLink;
}

function resolveQuickAddProductTitle(newHtml, fallbackTitle = '') {
  return (
    newHtml.querySelector('.view-product-title a')?.textContent?.trim() ||
    newHtml.querySelector('.product-header a')?.textContent?.trim() ||
    fallbackTitle
  );
}

function buildQuickAddModalContent(newHtml, { productPageUrl, productTitle = '' }) {
  const productGrid = newHtml.querySelector('[data-product-grid-content]');
  if (!productGrid) return null;

  if (isMobileBreakpoint()) {
    const productDetails = productGrid.querySelector('.product-details');
    const productFormComponent = productGrid.querySelector('product-form-component');
    const variantPicker = productGrid.querySelector('variant-picker, swatches-variant-picker-component');
    const productPrice = productGrid.querySelector('product-price');
    const resolvedTitle = resolveQuickAddProductTitle(newHtml, productTitle);

    if (!productDetails || !productFormComponent || !variantPicker || !productPrice || !resolvedTitle) return null;

    const productHeader = document.createElement('div');
    productHeader.classList.add('product-header');

    const productTitleLink = document.createElement('a');
    productTitleLink.textContent = resolvedTitle;
    productTitleLink.href = productPageUrl;

    productHeader.appendChild(productTitleLink);
    productHeader.appendChild(productPrice);
    productGrid.appendChild(productHeader);
    productGrid.appendChild(variantPicker);
    productGrid.appendChild(productFormComponent);
    productGrid.appendChild(createViewProductDetailsLink(productPageUrl));

    productDetails.remove();
  } else {
    const productDetails = productGrid.querySelector('.product-details .group-block-content');

    if (productDetails) {
      productDetails.appendChild(createViewProductDetailsLink(productPageUrl));
    }
  }

  return productGrid;
}

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {Document | null} */
  #cachedProductHtml = null;

  get cachedProductHtml() {
    return this.#cachedProductHtml;
  }

  get productPageUrl() {
    return /** @type {HTMLAnchorElement} */ (this.closest('product-card')?.querySelector('a[ref="productCardLink"]'))
      ?.href;
  }

  get trackingContextData() {
    const listTrackingParams = parseTrackingJson(
      this.closest('[data-track-view="view_item_list"], [data-track-view="view_search_results"]')?.getAttribute(
        'data-track-params'
      )
    );
    const itemTrackingContext = parseTrackingJson(
      this.closest('[data-track-context]')?.getAttribute('data-track-context')
    );

    return Object.assign({}, listTrackingParams, itemTrackingContext);
  }

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
  }

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();

    if (!this.#cachedProductHtml) {
      await this.fetchProductPage(this.productPageUrl);
    }

    if (this.#cachedProductHtml) {
      // Create a fresh copy of the cached HTML to avoid modifying the original
      const freshHtmlCopy = new DOMParser().parseFromString(
        this.#cachedProductHtml.documentElement.outerHTML,
        'text/html'
      );

      await this.updateQuickAddModal(freshHtmlCopy);
    }

    this.#openQuickAddModal();
  };

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    this.#stayVisibleUntilDialogCloses(dialogComponent);

    dialogComponent.showDialog();
  };

  #buildModalTrackingData(root) {
    const viewItemElement = root.querySelector('[data-track-view="view_item"]');
    const viewItemTrackingParams = parseTrackingJson(viewItemElement?.getAttribute('data-track-params'));

    if (viewItemTrackingParams.items != null) {
      delete viewItemTrackingParams.items;
    }

    return {
      modalTrackingData: Object.assign({}, this.trackingContextData, viewItemTrackingParams, {
        interaction_location: 'quick_add_popup',
      }),
      viewItemElement,
    };
  }
  /**
   * Adds tracking metadata to the quick add modal and forms.
   * @param {ParentNode} root
   * @param {Record<string, unknown>} modalTrackingData
   * @param {Element | null} viewItemElement
   */

  #decorateQuickAddTracking(root, modalTrackingData, viewItemElement) {
    root.querySelectorAll('form[data-type="add-to-cart-form"]').forEach((form) => {
      form.dataset.trackSubmit = 'quick_add';
      delete form.dataset.trackSubmitExtra;
    });

    const serializedTrackingData = Object.keys(modalTrackingData).length ? JSON.stringify(modalTrackingData) : '';

    if (serializedTrackingData) {
      root.setAttribute('data-track-context', serializedTrackingData);
    }
    else {
      root.removeAttribute('data-track-context');
    }

    if (viewItemElement instanceof Element) {
      viewItemElement.removeAttribute('data-track-view');
      viewItemElement.removeAttribute('data-track-view-mode');
    }

    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof HTMLElement)) return;

    dialogComponent.dataset.trackOpen = 'view_item';
    dialogComponent.dataset.trackOpenExtra = 'quick_add_open';
    dialogComponent.removeAttribute('data-track-params');

    if (serializedTrackingData) {
      dialogComponent.setAttribute('data-track-context', serializedTrackingData);
    }
    else {
      dialogComponent.removeAttribute('data-track-context');
    }
  }

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * Fetches the product page content
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<void>}
   * @throws {Error} If the fetch request fails or returns a non-200 response
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return;

    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      this.#cachedProductHtml = await fetchProductPageHtml(productPageUrl, this.#abortController.signal);
    } catch (error) {
      if (error.name === 'AbortError') {
        return;
      } else {
        throw error;
      }
    } finally {
      this.#abortController = null;
    }
  }

  /**
   * Re-renders the variant picker.
   * @param {Document} newHtml - The new HTML.
   */
  async updateQuickAddModal(newHtml) {
    const modalContent = document.getElementById('quick-add-modal-content');

    if (!modalContent) return;

    const productGrid = buildQuickAddModalContent(newHtml, {
      productPageUrl: this.productPageUrl,
      productTitle: this.dataset.productTitle || '',
    });

    if (!productGrid) return;
    const { modalTrackingData, viewItemElement } = this.#buildModalTrackingData(productGrid);
    this.#decorateQuickAddTracking(productGrid, modalTrackingData, viewItemElement);
    morph(modalContent, productGrid);
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();
  /** @type {AbortController | null} */
  #productSwitchController = null;
  /** @type {Map<string, string>} */
  #productHtmlCache = new Map();

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate, { signal: this.#abortController.signal });
    this.addEventListener(ThemeEvents.variantUpdate, this.#updateProductTitleLink);
    this.addEventListener('click', this.#handleRelatedProductClick, { signal: this.#abortController.signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
  }

  /**
   * Closes the dialog
   * @param {CartUpdateEvent} event - The cart update event
   */
  handleCartUpdate = (event) => {
    if (event.detail.data.didError) return;
    this.closeDialog();
  };

  #handleRelatedProductClick = async (event) => {
    if (!(event.target instanceof Element)) return;

    const relatedProductLink = event.target.closest('.related-color-item[href]');
    if (!(relatedProductLink instanceof HTMLAnchorElement)) return;

    event.preventDefault();

    if (relatedProductLink.getAttribute('aria-current') === 'page') return;

    relatedProductLink.setAttribute('aria-busy', 'true');

    try {
      await this.#switchProduct(relatedProductLink.href);
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') return;
      console.error('[quick-add] failed to switch product', error);
    } finally {
      relatedProductLink.removeAttribute('aria-busy');
    }
  };

  async #switchProduct(productPageUrl) {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return;

    this.#productSwitchController?.abort();
    this.#productSwitchController = new AbortController();

    let htmlText = this.#productHtmlCache.get(productPageUrl);

    if (!htmlText) {
      const response = await fetch(productPageUrl, { signal: this.#productSwitchController.signal });

      if (!response.ok) {
        throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      }

      htmlText = await response.text();
      this.#productHtmlCache.set(productPageUrl, htmlText);
    }

    const newHtml = new DOMParser().parseFromString(htmlText, 'text/html');
    const productGrid = buildQuickAddModalContent(newHtml, {
      productPageUrl,
      productTitle: resolveQuickAddProductTitle(newHtml),
    });

    if (!productGrid) return;

    morph(modalContent, productGrid);
    modalContent.scrollTop = 0;
  }

  #updateProductTitleLink = (/** @type {CustomEvent} */ event) => {
    const anchorElement = /** @type {HTMLAnchorElement} */ (
      event.detail.data.html?.querySelector('.view-product-title a')
    );
    const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-product-title a'));
    const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.product-header a'));
    const viewProductDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-product-details-link'));

    if (!anchorElement) return;

    if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
    if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
    if (viewProductDetailsLink) viewProductDetailsLink.href = anchorElement.href;
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}
